// Bildhjälp för Varumärke: allt körs i webbläsaren vid uppladdning (ingen server).
// - hasTransparency: har bilden genomskinlig bakgrund? (PNG/SVG/WebP med alfa)
// - makeLightVersion: ljus version för mörka ytor – mörka/grå toner blir vita/ljusgrå, färger behålls
// - makeSquareIcon: kvadratisk appikon 512×512, beskuren från mitten
import { supabase } from './supabaseClient';

export const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
export const MAX_SIZE_MB = 2;

export function validateImageFile(file) {
  if (!ACCEPTED_TYPES.includes(file.type)) return 'Filen behöver vara en bild: PNG, JPG, SVG eller WebP.';
  if (file.size > MAX_SIZE_MB * 1024 * 1024) return `Bilden är för stor. Den får vara högst ${MAX_SIZE_MB} MB.`;
  return null;
}

export function loadImageFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Bilden gick inte att läsa.')); };
    img.src = url;
  });
}

function naturalSize(img) {
  // SVG utan width/height kan sakna naturlig storlek – ge den en rimlig.
  const w = img.naturalWidth || 800;
  const h = img.naturalHeight || Math.round(w / 2.5);
  return { w, h };
}

function drawToCanvas(img, maxW) {
  const { w, h } = naturalSize(img);
  const scale = Math.min(1, maxW / w);
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * scale));
  c.height = Math.max(1, Math.round(h * scale));
  c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
  return c;
}

const toBlob = (canvas) => new Promise(res => canvas.toBlob(res, 'image/png'));

export function hasTransparency(img) {
  const c = drawToCanvas(img, 400);
  const d = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
  let t = 0;
  for (let i = 3; i < d.length; i += 4) if (d[i] < 250) t++;
  return t / (d.length / 4) > 0.01;
}

export async function makeLightVersion(img) {
  const c = drawToCanvas(img, 1600);
  const x = c.getContext('2d');
  const id = x.getImageData(0, 0, c.width, c.height);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    const sat = Math.max(r, g, b) - Math.min(r, g, b);
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    if (sat < 40) {
      // gråskala: svart → vitt, mellangrått → ljusgrått (behåller tvåtonade ordmärken)
      const v = Math.round(255 - lum * 0.5);
      d[i] = d[i + 1] = d[i + 2] = v;
    } else if (lum < 100) {
      // mörk färg (t.ex. mörkblå text) syns inte på mörk botten → vit
      d[i] = d[i + 1] = d[i + 2] = 255;
    }
  }
  x.putImageData(id, 0, 0);
  return toBlob(c);
}

export async function makeSquareIcon(img) {
  const { w, h } = naturalSize(img);
  const side = Math.min(w, h);
  const c = document.createElement('canvas');
  c.width = 512; c.height = 512;
  const x = c.getContext('2d');
  x.fillStyle = '#FFFFFF'; // hemskärmar visar genomskinligt som svart
  x.fillRect(0, 0, 512, 512);
  x.drawImage(img, (w - side) / 2, (h - side) / 2, side, side, 0, 0, 512, 512);
  return toBlob(c);
}

export async function uploadBrandImage(therapistId, fileOrBlob, suffix, ext) {
  const fileName = `${therapistId}/${Date.now()}-${suffix}.${ext}`;
  const { error } = await supabase.storage.from('logos').upload(fileName, fileOrBlob, { upsert: true, contentType: fileOrBlob.type || 'image/png' });
  if (error) throw new Error('Uppladdningen misslyckades. Försök igen.');
  return supabase.storage.from('logos').getPublicUrl(fileName).data.publicUrl;
}

export function fileExt(file) {
  return ({ 'image/png': 'png', 'image/jpeg': 'jpg', 'image/svg+xml': 'svg', 'image/webp': 'webp' })[file.type] || 'png';
}
